/**
 * useSchemaHealth Hook
 *
 * Provides schema health data and operations for the Schema Health tab.
 * Fetches stats, orphaned schemas, and archived schemas from the API,
 * and exposes drop/cleanup actions.
 *
 * Part of: AI Platform Phase 3.5.4 - Schema Cleanup UI
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

export interface SchemaStats {
  total_schemas: number;
  active_schemas: number;
  orphaned_schemas: number;
  archived_schemas: number;
  orphaned_size_bytes: number;
  orphaned_size_mb: number;
  archived_size_bytes: number;
  archived_size_mb: number;
}

export interface OrphanedSchema {
  name: string;
  size_bytes: number;
  size_mb: number;
  table_count: number;
  is_archived: boolean;
}

export interface ArchivedSchema {
  name: string;
  archived_at: string | null;
  size_bytes: number;
  size_mb: number;
  table_count: number;
}

export interface CleanupResult {
  dry_run: boolean;
  schemas_processed: number;
  results: Array<{
    schema: string;
    action: string;
    error?: string;
  }>;
  summary: {
    deleted?: number;
    would_delete?: number;
    failed: number;
    skipped: number;
  };
}

export interface UseSchemaHealthReturn {
  stats: SchemaStats | null;
  orphaned: OrphanedSchema[];
  archived: ArchivedSchema[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  dropSchema: (name: string) => Promise<boolean>;
  cleanupOrphaned: (dryRun?: boolean) => Promise<CleanupResult | null>;
  cleanupArchived: (olderThanDays?: number, dryRun?: boolean) => Promise<CleanupResult | null>;
}

// =============================================================================
// Hook
// =============================================================================

export function useSchemaHealth(): UseSchemaHealthReturn {
  const { authFetch } = useAuth();
  const [stats, setStats] = useState<SchemaStats | null>(null);
  const [orphaned, setOrphaned] = useState<OrphanedSchema[]>([]);
  const [archived, setArchived] = useState<ArchivedSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, orphanedRes, archivedRes] = await Promise.all([
        authFetch('/api/admin/schemas/stats'),
        authFetch('/api/admin/schemas/orphaned'),
        authFetch('/api/admin/schemas/archived?older_than_days=0'),
      ]);

      if (!statsRes.ok) {
        const err = await statsRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch schema stats');
      }
      if (!orphanedRes.ok) {
        const err = await orphanedRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch orphaned schemas');
      }
      if (!archivedRes.ok) {
        const err = await archivedRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch archived schemas');
      }

      const statsData = await statsRes.json();
      const orphanedData = await orphanedRes.json();
      const archivedData = await archivedRes.json();

      setStats(statsData);
      setOrphaned(orphanedData.orphaned || []);
      setArchived(archivedData.archived || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schema health data');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const dropSchema = useCallback(async (name: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/api/admin/schemas/${encodeURIComponent(name)}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to drop schema "${name}"`);
      }
      await refresh();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to drop schema');
      return false;
    }
  }, [authFetch, refresh]);

  const cleanupOrphaned = useCallback(async (dryRun = false): Promise<CleanupResult | null> => {
    try {
      const response = await authFetch(
        `/api/admin/schemas/cleanup/orphaned?dry_run=${dryRun}`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to cleanup orphaned schemas');
      }
      const result = await response.json();
      if (!dryRun) {
        await refresh();
      }
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup orphaned schemas');
      return null;
    }
  }, [authFetch, refresh]);

  const cleanupArchived = useCallback(async (
    olderThanDays = 30,
    dryRun = false,
  ): Promise<CleanupResult | null> => {
    try {
      const response = await authFetch(
        `/api/admin/schemas/cleanup/archived?older_than_days=${olderThanDays}&dry_run=${dryRun}`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to cleanup archived schemas');
      }
      const result = await response.json();
      if (!dryRun) {
        await refresh();
      }
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup archived schemas');
      return null;
    }
  }, [authFetch, refresh]);

  return {
    stats,
    orphaned,
    archived,
    loading,
    error,
    refresh,
    dropSchema,
    cleanupOrphaned,
    cleanupArchived,
  };
}
